import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import {
  type WebhookCreated,
  type WebhookSubscription,
  webhooksApi,
} from '@/api/webhooks.api';
import { AppShell } from '@/components/layout/AppShell';
import { formatDateTime } from '@/lib/format';

export function WebhooksPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newHook, setNewHook] = useState<WebhookCreated | null>(null);
  const [openDeliveries, setOpenDeliveries] = useState<string | null>(null);

  const { data: hooks = [] } = useQuery({
    queryKey: ['webhooks', 'list'],
    queryFn: () => webhooksApi.list(),
  });
  const { data: events = [] } = useQuery({
    queryKey: ['webhooks', 'events'],
    queryFn: () => webhooksApi.listEvents(),
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; url: string; events: string[] }) =>
      webhooksApi.create(input),
    onSuccess: (data) => {
      setNewHook(data);
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['webhooks', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => webhooksApi.delete(uuid),
    onSuccess: () => {
      toast.success(t('settings_webhooks.deleted'));
      qc.invalidateQueries({ queryKey: ['webhooks', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('settings.webhooks_title')}</h1>
        <p className="page-subtitle">{t('settings.webhooks_subtitle')}</p>
      </div>

      <div className="card mb-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900 m-0">{t('settings_webhooks.subscriptions')}</h3>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
            {t('settings_webhooks.new_btn')}
          </button>
        </div>

        {hooks.length === 0 ? (
          <div className="text-center py-8 text-muted text-[13px]">
            {t('settings_webhooks.no_hooks')}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('settings_webhooks.th_name')}</th>
                <th>URL</th>
                <th>{t('settings_webhooks.th_events')}</th>
                <th>{t('settings_webhooks.th_last_delivery')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {hooks.map((h) => (
                <tr key={h.id}>
                  <td className="font-medium text-gray-900">{h.name}</td>
                  <td>
                    <code className="text-[12px] text-muted">{truncate(h.url, 40)}</code>
                  </td>
                  <td className="text-[11px] text-muted">
                    {t('settings_webhooks.events_count', { count: h.events.length })}
                  </td>
                  <td className="text-[11px] text-muted">
                    {h.lastDeliveryAt ? formatDateTime(h.lastDeliveryAt) : '—'}
                    {h.lastErrorAt && (
                      <div className="text-danger text-[10px]">
                        {t('settings_webhooks.last_error')}: {formatDateTime(h.lastErrorAt)}
                      </div>
                    )}
                  </td>
                  <td className="text-right space-x-1">
                    <button
                      onClick={() => setOpenDeliveries(h.id)}
                      className="btn btn-ghost btn-sm"
                    >
                      {t('settings_webhooks.deliveries')}
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(t('settings_webhooks.delete_confirm', { name: h.name }))) {
                          deleteMutation.mutate(h.id);
                        }
                      }}
                      className="btn btn-ghost btn-sm text-danger"
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateWebhookModal
          availableEvents={events}
          onClose={() => setShowCreate(false)}
          onCreate={(input) => createMutation.mutate(input)}
          pending={createMutation.isPending}
        />
      )}

      {newHook && <NewWebhookSecretModal hook={newHook} onClose={() => setNewHook(null)} />}

      {openDeliveries && (
        <DeliveriesModal
          subscription={hooks.find((h) => h.id === openDeliveries)}
          onClose={() => setOpenDeliveries(null)}
        />
      )}
    </AppShell>
  );
}

function CreateWebhookModal({
  availableEvents,
  onClose,
  onCreate,
  pending,
}: {
  availableEvents: string[];
  onClose: () => void;
  onCreate: (input: { name: string; url: string; events: string[] }) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('https://');
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set(availableEvents));

  function toggle(ev: string) {
    setSelectedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) next.delete(ev);
      else next.add(ev);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('settings_webhooks.new_btn')}</h2>
          <button onClick={onClose} className="text-muted hover:text-gray-900 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label">{t('settings_webhooks.name_label')}</label>
            <input
              className="input"
              placeholder={t('settings_webhooks.name_placeholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label">{t('settings_webhooks.url_label')}</label>
            <input
              className="input"
              placeholder="https://api.example.com/webhooks/esd"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <div>
            <label className="label">{t('settings_webhooks.th_events')}</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {availableEvents.map((ev) => (
                <label
                  key={ev}
                  className="flex items-center gap-2 px-2 py-1.5 border border-border rounded-md text-[12px] cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedEvents.has(ev)}
                    onChange={() => toggle(ev)}
                    className="accent-primary"
                  />
                  <code>{ev}</code>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
          <button
            onClick={() => {
              if (!url || !/^https?:\/\//i.test(url)) {
                toast.error(t('settings_webhooks.url_invalid'));
                return;
              }
              if (selectedEvents.size === 0) {
                toast.error(t('settings_webhooks.select_event'));
                return;
              }
              onCreate({
                name: name.trim() || t('settings_webhooks.untitled'),
                url: url.trim(),
                events: Array.from(selectedEvents),
              });
            }}
            disabled={pending}
            className="btn btn-primary"
          >
            {pending ? t('common.creating') : t('settings_webhooks.create_btn')}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewWebhookSecretModal({ hook, onClose }: { hook: WebhookCreated; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl">
        <div className="p-5 border-b border-border">
          <h2 className="text-lg font-semibold">{t('settings_webhooks.save_secret_title')}</h2>
          <p className="text-[12px] text-muted mt-1">
            {t('settings_webhooks.save_secret_text')}
          </p>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label text-muted">{t('settings_webhooks.secret_label')}</label>
            <div className="bg-gray-50 border border-border rounded-md p-3 font-mono text-[12px] break-all">
              {hook.secret}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(hook.secret);
                toast.success(t('common.copied'));
              }}
              className="btn btn-secondary btn-sm mt-2"
            >
              {t('common.copy')}
            </button>
          </div>
          <div className="bg-warning-bg border border-warning text-[12px] text-warning-fg rounded-md p-3">
            {t('settings_webhooks.secret_warning')}
          </div>
        </div>
        <div className="p-5 border-t border-border flex justify-end">
          <button onClick={onClose} className="btn btn-primary">{t('settings_integrations.done')}</button>
        </div>
      </div>
    </div>
  );
}

function DeliveriesModal({
  subscription,
  onClose,
}: {
  subscription: WebhookSubscription | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: deliveries = [], isLoading } = useQuery({
    queryKey: ['webhooks', 'deliveries', subscription?.id],
    queryFn: () => (subscription ? webhooksApi.listDeliveries(subscription.id) : Promise.resolve([])),
    enabled: !!subscription,
  });

  const retryMutation = useMutation({
    mutationFn: (deliveryId: string) => webhooksApi.retryDelivery(deliveryId),
    onSuccess: () => {
      toast.success(t('settings_webhooks.redelivered'));
      qc.invalidateQueries({ queryKey: ['webhooks', 'deliveries', subscription?.id] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  if (!subscription) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t('settings_webhooks.deliveries')}: {subscription.name}</h2>
            <p className="text-[12px] text-muted mt-0.5">{t('settings_webhooks.last_50')}</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-gray-900 text-xl leading-none">×</button>
        </div>
        <div className="p-2">
          {isLoading ? (
            <div className="text-center py-8 text-muted text-[13px]">{t('common.loading')}</div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-8 text-muted text-[13px]">
              {t('settings_webhooks.no_deliveries')}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('settings_webhooks.th_when')}</th>
                  <th>{t('settings_webhooks.th_event')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('settings_webhooks.th_attempts')}</th>
                  <th>{t('settings_webhooks.th_error_col')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => {
                  const ok = d.deliveredAt !== null;
                  return (
                    <tr key={d.id}>
                      <td className="text-[12px] text-muted">{formatDateTime(d.createdAt)}</td>
                      <td><code className="text-[12px]">{d.event}</code></td>
                      <td>
                        <span className={`badge ${ok ? 'badge-success' : 'badge-danger'} text-[10px]`}>
                          {ok ? d.statusCode : (d.statusCode ?? t('settings_webhooks.failed_status'))}
                        </span>
                      </td>
                      <td className="text-[12px]">{d.attempts}</td>
                      <td className="text-[11px] text-danger max-w-[200px] truncate">
                        {d.errorMessage ?? ''}
                      </td>
                      <td className="text-right">
                        {!ok && (
                          <button
                            onClick={() => retryMutation.mutate(d.id)}
                            className="btn btn-ghost btn-sm"
                            disabled={retryMutation.isPending}
                          >
                            {t('settings_webhooks.retry')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
